import { FC, useEffect, useState } from "react";
import { useParams } from "react-router";

import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { IconButton } from "@mui/material";

import { SATOSLink } from "@/components/Application/SATOSLink";

import { ProcedureDetailNode } from "@/model/Procedure/ProcedureDetailNode";

import { useQueryParamHide } from "@/service/Hooks";
import { useFetchActiveProcedureDetails } from "@/service/ProcedureQueries";
import { RouteParams } from "@/service/Utilities";

interface ChecklistNavigationControlsProps {
	activeSchemeId: number | undefined;
	setActiveSchemeId: (schemeId: number) => void;
	wrapControls?: boolean;
}

export const ChecklistNavigationControls: FC<
	ChecklistNavigationControlsProps
> = (props) => {
	const [activeChildNode, setActiveChildNode] = useState<
		ProcedureDetailNode | undefined
	>();
	const { schemeId } = useParams<RouteParams>();
	const checklistId = parseInt(schemeId!);
	const [hide, setHide] = useState("");
	const queryHide = useQueryParamHide();
	const { data: currentActiveDetails } = useFetchActiveProcedureDetails();

	useEffect(() => {
		setHide(queryHide ? "hide=1" : "");
	}, [queryHide]);

	useEffect(() => {
		if (!currentActiveDetails) {
			return;
		}

		const parentNode = currentActiveDetails?.ProcedureDetailNodes.find((node) =>
			node.Children.find((child) => child.SchemeId === checklistId),
		);
		const thisNode = parentNode?.Children.find(
			(child) => child.SchemeId === checklistId,
		);

		if (thisNode?.SchemeId && thisNode.SchemeId !== activeChildNode?.SchemeId) {
			setActiveChildNode(thisNode);

			if (props.activeSchemeId !== thisNode.SchemeId) {
				props.setActiveSchemeId(thisNode.SchemeId);
			}
		}
	}, [currentActiveDetails, checklistId, activeChildNode?.SchemeId]);

	const getClassName = (nextNodeId: number | undefined) => {
		return nextNodeId ? "button-icon" : "button-icon button--inactive";
	};

	const navigationControls = (
		<>
			{activeChildNode?.PreviousChecklist ? (
				<IconButton
					component={SATOSLink}
					to="AccessProcedureNode"
					params={[
						currentActiveDetails?.ProcedureId,
						activeChildNode?.PreviousChecklist,
					]}
					search={hide}
					className={getClassName(activeChildNode?.PreviousChecklist)}
				>
					<ArrowBackIosIcon fontSize="small" />
				</IconButton>
			) : (
				<IconButton disabled className="button-icon button--inactive">
					<ArrowBackIosIcon fontSize="small" />
				</IconButton>
			)}
			{activeChildNode?.NextChecklist ? (
				<IconButton
					component={SATOSLink}
					to="AccessProcedureNode"
					params={[
						currentActiveDetails?.ProcedureId,
						activeChildNode?.NextChecklist,
					]}
					search={hide}
					className={getClassName(activeChildNode?.NextChecklist)}
				>
					<ArrowForwardIosIcon fontSize="small" />
				</IconButton>
			) : (
				<IconButton disabled className="button-icon button--inactive">
					<ArrowForwardIosIcon fontSize="small" />
				</IconButton>
			)}
		</>
	);

	if (props.wrapControls) {
		return (
			<div className="heading-wrapper">
				<div className="heading-wrapper-tools">{navigationControls}</div>
			</div>
		);
	}

	return navigationControls;
};
